// src/pages/Lounge/SearchClubs/SearchClubs.jsx
import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion as Motion } from "framer-motion";
import Loading from "@/components/Loading/Loading";

import api from "@/utils/axios";

import MrNoty from "@/assets/UI/searchClub/searchMr.webp";
import MrsNoty from "@/assets/UI/searchClub/searchMrs.webp";
import CityImage from "@/assets/UI/city.webp";

import cls from "./SearchClubs.module.scss";

const floatSlow = {
  hidden: { opacity: 0.5 },
  show: {
    opacity: [0.5, 0.65, 0.5],
    scale: [1, 1.04, 1],
    transition: { duration: 10, ease: "easeInOut", repeat: Infinity },
  },
};

const floatSlowAlt = {
  hidden: { opacity: 0.5 },
  show: {
    opacity: [1, 0.62, 1],
    scale: [1.03, 1, 1.03],
    x: [0, -6, 0],
    transition: { duration: 12, ease: "easeInOut", repeat: Infinity },
  },
};

const slideInUp = {
  hidden: { opacity: 0, y: 18 },
  show: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 220, damping: 24, mass: 0.9 },
  },
};

const SearchClubs = () => {
  const { t } = useTranslation();
  const location = useLocation();

  const isPublic = location.pathname === "/lounge";
  const isFriends = location.pathname === "/lounge/friends-feed";
  const isClubs = location.pathname === "/lounge/clubs-feed";
  const isChat = location.pathname === "/lounge/chat";
  const isSearch = location.pathname === "/lounge/search";

  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let alive = true;

    const fetchCities = async () => {
      try {
        setLoading(true);
        setLoadError(false);

        const res = await api.get("/v1/lounge/cities/search");
        if (!alive) return;

        setCities(Array.isArray(res.data) ? res.data : []);
      } catch {
        if (!alive) return;
        setLoadError(true);
        setCities([]);
      } finally {
        if (alive) setLoading(false);
      }
    };

    fetchCities();
    return () => {
      alive = false;
    };
  }, []);

  // ✅ Loader gate AFTER hooks
  if (loading) {
    return (
      <Loading
        fullscreen
        titleKey="loading.title"
        subtitleKey="loading.subtitle"
        showTips
        cycleMs={1400}
      />
    );
  }

  return (
    <div className={cls.page}>
      <div className={cls.pageInner}>
        {/* Tabs row */}
        <nav className={cls.tabs}>
          <Link
            to="/lounge"
            className={`${cls.tabButton} ${isPublic ? cls.activeTab : ""}`}
          >
            {t("memberPublicFeed.tabs.public")}
          </Link>
          <Link
            to="/lounge/friends-feed"
            className={`${cls.tabButton} ${isFriends ? cls.activeTab : ""}`}
          >
            {t("memberPublicFeed.tabs.friends")}
          </Link>
          <Link
            to="/lounge/clubs-feed"
            className={`${cls.tabButton} ${isClubs ? cls.activeTab : ""}`}
          >
            {t("memberPublicFeed.tabs.clubs")}
          </Link>
          <Link
            to="/lounge/chat"
            className={`${cls.tabButton} ${isChat ? cls.activeTab : ""}`}
          >
            {t("memberPublicFeed.tabs.chat")}
          </Link>
          <Link
            to="/lounge/search"
            className={`${cls.tabButton} ${
              isSearch ? `${cls.activeTab} ${cls.activeSearchTab}` : ""
            }`}
          >
            🔍 {t("memberPublicFeed.tabs.search")}
          </Link>
        </nav>

        <div className={cls.layout}>
          <div className={cls.rightColumn}>
            {/* Clubs CTA */}
            <section className={cls.card}>
              <div className={cls.cardHeaderRow}>
                <h2 className={cls.cardTitle}>{t("memberClubsFeed.title")}</h2>
              </div>

              <div className={cls.clubActionsRow}>
                <Link
                  to="/lounge/my-clubs"
                  className={`${cls.clubMiniBtn} ${cls.btnMyClubs}`}
                >
                  {t("memberClubsFeed.cta.myClubs.label")}
                </Link>

                <Link
                  to="/lounge/explore-universe"
                  className={`${cls.clubMiniBtn} ${cls.btnExplore}`}
                >
                  {t("memberClubsFeed.cta.exploreUniverse.label")}
                </Link>

                <Link
                  to="/lounge/search-clubs"
                  className={`${cls.clubMiniBtn} ${cls.btnSearch} ${cls.btnSearchActive}`}
                >
                  {t("memberClubsFeed.cta.searchClubs.label")}
                </Link>
              </div>
            </section>

            {/* Hero */}
            <section className={cls.searchHero}>
              <div className={cls.searchHeroStage}>
                <Motion.div
                  className={`${cls.heroFrame} ${cls.heroArtFrame}`}
                  variants={slideInUp}
                  initial="hidden"
                  animate="show"
                  aria-hidden="true"
                >
                  <div className={cls.heroArtWrap}>
                    <Motion.img
                      src={MrNoty}
                      alt=""
                      className={`${cls.heroArt} ${cls.heroMr}`}
                      variants={floatSlow}
                      initial="hidden"
                      animate="show"
                    />
                    <Motion.img
                      src={MrsNoty}
                      alt=""
                      className={`${cls.heroArt} ${cls.heroMrs}`}
                      variants={floatSlowAlt}
                      initial="hidden"
                      animate="show"
                    />
                  </div>
                </Motion.div>

                <Motion.div
                  className={`${cls.heroFrame} ${cls.heroCard}`}
                  variants={slideInUp}
                  initial="hidden"
                  animate="show"
                >
                  <span className={cls.searchPromptLabel}>
                    {t("searchClubs.hero.label")}
                  </span>
                  <h2 className={cls.searchPromptTitle}>
                    {t("searchClubs.hero.title")}
                  </h2>
                  <p className={cls.searchPromptText}>
                    {t("searchClubs.hero.subtitle")}
                  </p>
                </Motion.div>
              </div>
            </section>

            {/* City list */}
            <section className={cls.cityListSection}>
              <div className={cls.cityListHeader}>
                <h3 className={cls.cityListTitle}>
                  {t("searchClubs.cityList.title")}
                </h3>
                <p className={cls.cityListMeta}>
                  {t("searchClubs.cityList.meta")}
                </p>
                {loadError && (
                  <p className={cls.cityListError}>
                    {t("searchClubs.cityList.error")}
                  </p>
                )}
              </div>

              <div className={cls.cityList}>
                {!loadError && cities.length === 0 && (
                  <p className={cls.cityListEmpty}>
                    {t("searchClubs.cityList.empty")}
                  </p>
                )}

                {cities.map((city) => {
                  const thumbUrl = city?.avatarThumb?.url || CityImage;

                  return (
                    <Motion.article
                      key={city.cityId}
                      className={cls.cityCard}
                      whileHover={{ y: -2, scale: 1.01 }}
                      transition={{ duration: 0.14 }}
                    >
                      <div className={cls.cityThumb}>
                        <img
                          src={thumbUrl}
                          alt={city.cityName}
                          className={cls.cityThumbImg}
                        />
                      </div>

                      <div className={cls.cityContent}>
                        <div className={cls.cityText}>
                          <p className={cls.cityName}>{city.cityName}</p>
                          <p className={cls.cityVibeText}>{city.cityVibe}</p>
                        </div>

                        <Link
                          to={`/lounge/search-clubs/city/${city.cityId}`}
                          className={cls.cityButton}
                        >
                          {t("searchClubs.cityCard.button")}
                        </Link>
                      </div>
                    </Motion.article>
                  );
                })}
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchClubs;
